/**
 * VALEN VARET · Barra de anuncios promocional rotativa.
 *
 * - Rotacion automatica en bucle (intervalo configurable desde el editor).
 * - Deslizamiento manual con el dedo (movil/tablet) o arrastrando con el raton.
 * - Navegacion con flechas y con el teclado.
 * - Se pausa al pasar el cursor por encima, al enfocar con teclado y cuando
 *   la pestana deja de estar visible.
 *
 * No depende de ningun modulo interno del tema, para que siga funcionando
 * aunque Shopify actualice los componentes de Horizon.
 */

const SWIPE_THRESHOLD_PX = 40;

class VVPromoBar extends HTMLElement {
  /** @type {HTMLElement[]} */
  slides = [];
  /** @type {number} */
  index = 0;
  /** @type {number|undefined} */
  timer = undefined;
  /** @type {number|null} */
  pointerStartX = null;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-vv-slide]'));
    if (this.slides.length === 0) return;

    this.show(0, 1);

    if (this.slides.length > 1) {
      this.querySelector('[data-vv-prev]')?.addEventListener('click', this.handlePrev);
      this.querySelector('[data-vv-next]')?.addEventListener('click', this.handleNext);

      this.addEventListener('pointerdown', this.handlePointerDown);
      this.addEventListener('pointerup', this.handlePointerUp);
      this.addEventListener('pointercancel', this.handlePointerCancel);
      this.addEventListener('keydown', this.handleKeydown);

      this.addEventListener('mouseenter', this.pause);
      this.addEventListener('mouseleave', this.play);
      this.addEventListener('focusin', this.pause);
      this.addEventListener('focusout', this.play);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);

      this.play();
    } else {
      // Con un solo anuncio no tiene sentido mostrar los controles.
      this.querySelector('[data-vv-prev]')?.setAttribute('hidden', '');
      this.querySelector('[data-vv-next]')?.setAttribute('hidden', '');
    }
  }

  disconnectedCallback() {
    this.pause();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  /** Intervalo de rotacion en milisegundos (0 = desactivado). */
  get interval() {
    const value = parseInt(this.dataset.autoplay ?? '', 10);
    return Number.isNaN(value) ? 0 : value;
  }

  /**
   * Muestra la diapositiva indicada.
   * @param {number} next - Indice destino (se normaliza para el bucle).
   * @param {number} direction - 1 hacia delante, -1 hacia atras.
   */
  show(next, direction) {
    const total = this.slides.length;
    const target = ((next % total) + total) % total;

    this.slides.forEach((slide, i) => {
      const isActive = i === target;
      slide.classList.toggle('is-active', isActive);
      slide.classList.toggle('is-from-left', !isActive && direction < 0);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      if (isActive) {
        slide.removeAttribute('inert');
      } else {
        slide.setAttribute('inert', '');
      }
    });

    this.index = target;
  }

  next() {
    this.show(this.index + 1, 1);
  }

  previous() {
    this.show(this.index - 1, -1);
  }

  play = () => {
    if (this.interval <= 0 || this.timer) return;
    this.timer = window.setInterval(() => {
      if (document.hidden || this.matches(':hover')) return;
      this.next();
    }, this.interval);
  };

  pause = () => {
    if (!this.timer) return;
    window.clearInterval(this.timer);
    this.timer = undefined;
  };

  /** Reinicia el temporizador tras una interaccion manual. */
  restart() {
    this.pause();
    this.play();
  }

  handlePrev = () => {
    this.previous();
    this.restart();
  };

  handleNext = () => {
    this.next();
    this.restart();
  };

  /** @param {PointerEvent} event */
  handlePointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.pointerStartX = event.clientX;
    this.pause();
  };

  /** @param {PointerEvent} event */
  handlePointerUp = (event) => {
    if (this.pointerStartX === null) return;

    const delta = event.clientX - this.pointerStartX;
    this.pointerStartX = null;

    if (Math.abs(delta) >= SWIPE_THRESHOLD_PX) {
      if (delta < 0) {
        this.next();
      } else {
        this.previous();
      }
    }

    this.play();
  };

  handlePointerCancel = () => {
    this.pointerStartX = null;
    this.play();
  };

  /** @param {KeyboardEvent} event */
  handleKeydown = (event) => {
    if (event.key === 'ArrowRight') {
      this.handleNext();
    } else if (event.key === 'ArrowLeft') {
      this.handlePrev();
    }
  };

  handleVisibilityChange = () => {
    if (document.hidden) {
      this.pause();
    } else {
      this.play();
    }
  };
}

if (!customElements.get('vv-promo-bar')) {
  customElements.define('vv-promo-bar', VVPromoBar);
}